<template>
  <div class="scroll-container">
    <!-- Fade indicators -->
    <div v-show="canScrollLeft" class="fade-left" />
    <div v-show="canScrollRight" class="fade-right" />

    <!-- Navigation arrows -->
    <button
      v-show="canScrollLeft"
      class="nav-arrow left"
      :class="{ 'has-custom-arrow': leftArrowSrc }"
      @click="scrollLeft"
    >
      <img v-if="leftArrowSrc" :src="leftArrowSrc" alt="Scroll left" class="nav-arrow-img" />
      <span v-else>‹</span>
    </button>
    <button
      v-show="canScrollRight"
      class="nav-arrow right"
      :class="{ 'has-custom-arrow': rightArrowSrc }"
      @click="scrollRight"
    >
      <img v-if="rightArrowSrc" :src="rightArrowSrc" alt="Scroll right" class="nav-arrow-img" />
      <span v-else>›</span>
    </button>

    <!-- Skeleton loading state -->
    <div v-if="loading" class="scroll-content skeleton-buttons">
      <span v-for="n in 6" :key="n" class="btn-skeleton shimmer"></span>
    </div>

    <!-- Scrollable content -->
    <div v-else ref="containerRef" class="scroll-content" :class="{ dragging: isDragging }">
      <button
        ref="allButton"
        class="btn"
        :class="{ active: selectedItem === '' }"
        @click="selectItem({ id: 'all' })"
        part="sub-category-button"
      >
        All
      </button>
      <button
        v-for="item in items"
        :key="item.id"
        :ref="
          (el) => {
            if (el) itemRefs[item.id] = el;
          }
        "
        class="btn"
        :class="{ active: selectedItem === item.id }"
        @click="selectItem(item)"
        v-html="item.name"
      ></button>
    </div>

    <!-- Progress bar -->
    <div class="progress-container">
      <div class="progress-bar" :style="{ width: `${scrollPercentage}%` }" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from "vue";
import { useHorizontalScroll, useScrollArrows } from "../composables/useHorizontalScroll";
import { isMobileDevice } from "../utils/device.js";
import { useConfigStore } from "../stores/config.js";

// Props
const props = defineProps({
  items: {
    type: Array,
  },
  selectedItem: {
    type: Number,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits(["item-selected"]);

// Custom arrow images from config
const configStore = useConfigStore();
const leftArrowSrc = computed(() => configStore.scrollArrows?.left || null);
const rightArrowSrc = computed(() => configStore.scrollArrows?.right || null);

// Refs for button elements
const allButton = ref(null);
const itemRefs = ref({});

// Use composables
const {
  containerRef,
  isDragging,
  scrollPercentage,
  canScrollLeft,
  canScrollRight,
  scrollTo,
  updateScrollInfo,
} = useHorizontalScroll();

const { scrollLeft, scrollRight } = useScrollArrows(scrollTo);

// Center the active category
const centerActiveCategory = async () => {
  if (!isMobileDevice() || !containerRef.value) return;

  await nextTick();

  let activeButton = null;

  // Find the active button element
  if (props.selectedItem === "" || props.selectedItem === "all") {
    activeButton = allButton.value;
  } else {
    activeButton = itemRefs.value[props.selectedItem];
  }

  if (activeButton) {
    const container = containerRef.value;
    const containerWidth = container.clientWidth;
    const buttonLeft = activeButton.offsetLeft;
    const buttonWidth = activeButton.offsetWidth;

    // Calculate the scroll position to center the button
    const scrollLeft = buttonLeft - containerWidth / 2 + buttonWidth / 2;

    // Smooth scroll to the calculated position
    container.scrollTo({
      left: scrollLeft,
      behavior: "smooth",
    });

    // Update scroll info after scrolling
    setTimeout(() => {
      updateScrollInfo();
    }, 300);
  }
};

// Watch for items changes and update scroll info
watch(
  () => props.items,
  async (newItems) => {
    if (newItems && newItems.length > 0) {
      await nextTick();
      updateScrollInfo();
      // Center active category after items are loaded
      centerActiveCategory();
    }
  },
  { immediate: true },
);

// Watch for selectedItem changes and center if on mobile
watch(
  () => props.selectedItem,
  () => {
    centerActiveCategory();
  },
);

// Center on mount
onMounted(() => {
  centerActiveCategory();
});

// Methods
const selectItem = (item) => {
  emit("item-selected", item);
};
</script>

<style lang="scss">
/* Minimal styles for functionality */
.scroll-container {
  position: relative;
  overflow: hidden;
  display: flex;
}

.scroll-content {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  padding: 10px 0;
}

.scroll-content::-webkit-scrollbar {
  display: none;
}

.scroll-content:active,
.scroll-content.dragging {
  cursor: grabbing;
  scroll-behavior: auto;
}

.btn {
  padding: var(--vwc-sub-category-button-padding);
  margin-right: 10px;
  white-space: nowrap;
  flex-shrink: 0;
  border: var(--vwc-category-button-border);
  font-family: var(--vwc-font-body);
  background: var(--vwc-sub-category-button-background);
  border-radius: var(--vwc-category-button-border-radius);
  cursor: pointer;
  color: var(--vwc-sub-category-button-color);
  transition:
    background 0.3s ease,
    color 0.3s ease;
  position: relative;
  font-weight: var(--vwc-sub-category-button-font-weight);
}

.btn:hover {
  background: var(--vwc-sub-category-button-hover-background);
  color: var(--vwc-sub-category-button-hover-color);
}

.btn.active {
  background: var(--vwc-sub-category-button-active-background, var(--vwc-color-primary));
  color: var(--vwc-sub-category-button-active-color, var(--vwc-color-text-light));

  &:after {
    content: var(--vwc-sub-category-button-after, "");
    color: var(--vwc-color-primary);
    position: absolute;
    left: calc(50% - 10px);
    top: calc(100% - 18px);
    font-size: 30px;
    width: 20px;
    line-height: 50%;
  }
}

/* Visual indicators */
.fade-left,
.fade-right {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 20px;
  pointer-events: none;
  z-index: 2;
}

/* .fade-left {
  left: 0;
  background: linear-gradient(to right, rgba(255,255,255,0.9), transparent);
}

.fade-right {
  right: 0;
  background: linear-gradient(to left, rgba(255,255,255,0.9), transparent);
} */

.nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: #ffffffb3;
  color: var(--vwc-color-primary);
  border: none;
  cursor: pointer;
  z-index: 2;
  transition: opacity 0.3s ease;
  font-size: 30px;
  font-weight: bold;
  line-height: 0px;

  background: #ffffff;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.72) 30%,
    rgba(255, 255, 255, 1) 52%,
    rgba(255, 255, 255, 1) 100%
  );
}

.nav-arrow:hover {
  background: white;
}

.nav-arrow.left {
  left: 0;
  // padding: 20px 25px 20px 5px;
  background: linear-gradient(
    270deg,
    rgba(255, 255, 255, 0.06) 0%,
    rgba(255, 255, 255, 0.72) 30%,
    rgba(255, 255, 255, 1) 52%,
    rgba(255, 255, 255, 1) 100%
  );
}

.nav-arrow.right {
  right: 0;
  // padding: 20px 5px 20px 25px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.72) 30%,
    rgba(255, 255, 255, 1) 52%,
    rgba(255, 255, 255, 1) 100%
  );
}

.nav-arrow.has-custom-arrow {
  background: none;
  padding: 0;
}

.nav-arrow.has-custom-arrow:hover {
  background: none;
}

.nav-arrow-img {
  width: 45px;
  // height: 30px;
  // object-fit: contain;
}

.progress-container {
  height: 3px;
  background: #eee;
  margin-top: 10px;
}

.progress-bar {
  height: 100%;
  background: var(--vwc-color-primary);
  transition: width 0.3s ease;
}

.skeleton-buttons {
  pointer-events: none;
}

.btn-skeleton {
  display: inline-block;
  width: 90px;
  height: 2.2em;
  margin-right: 10px;
  flex-shrink: 0;
  border-radius: var(--vwc-category-button-border-radius);
  background-color: var(--vwc-color-secondary, #e0d6c8);
}

.shimmer {
  background: linear-gradient(
    90deg,
    var(--vwc-color-secondary, #e0d6c8) 25%,
    color-mix(in srgb, var(--vwc-color-secondary, #e0d6c8), white 40%) 50%,
    var(--vwc-color-secondary, #e0d6c8) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite ease-in-out;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
